export const bingoWaitingTimes = {
  endFullScreen: 2000,
  hideOverlay: 5000,
  'msg-1': 3000,
  'msg-2': 3000,
  finding: 3000,
  prizes: 5000,
  consolation: 5000,
  winnersAnnouncement: 5000,
  match: {
    fireNumberMatchedAnimation: 3500,
    stopNumberMatchedAnimation: 8000
  }
};

export const giftTimings = {
  endGiftEvent: 10000,
  findingWithoutCarousel: 6000
};

export const ballTimings = {
  travelMs: 8000,
  startSpin: 700,
  carouselStopPadding: 1500,
  uniqueWinnerDismiss: 1000
};

export const bingoEvents = {
  session: {
    CHAT_MESSAGE_SENT: 'App\\Events\\ChatMessageSent',
    BINGO_NUMBER_DRAWN: 'App\\Events\\BingoNumberDrawn',
    BINGO_GAME_STARTED: 'App\\Events\\BingoGameStarted',
    BINGO_GAME_COMPLETED: 'App\\Events\\BingoGameCompleted',
    BINGO_WINNERS_FOUND: 'App\\Events\\BingoWinnersFound',
    PRIZE_WINNERS_FOUND: 'App\\Events\\PrizeWinnersFound',
    GIFT_GIVEN: 'App\\Events\\GiftGiven',
    SESSION_OUTRO: 'App\\Events\\SessionOutro',
    SESSION_COMPLETED: 'App\\Events\\SessionCompleted'
  },
  customer: {
    BINGO_CARD_CREATED: 'App\\Events\\BingoCardCreated'
  },
  pusher: {
    SUBSCRIPTION_SUCCEEDED: 'pusher:subscription_succeeded',
    SUBSCRIPTION_ERROR: 'pusher:subscription_error',
    STATE_CHANGE: 'state_change'
  }
} as const;

// The only pusher-js connection state this feature branches on. The others
// (connecting, unavailable, failed, disconnected) are all "not connected" as far as
// the customer-facing banner cares, so they are compared by exclusion rather than
// getting their own constants nothing reads.
export const bingoPusherConnectionStates = {
  CONNECTED: 'connected'
} as const;

export const bingoChannels = {
  session: (sessionId: number) => `session.${sessionId}`,
  customer: (sessionId: number, uuid: string) =>
    `presence-customer.${sessionId}.${uuid}`
};

export const bingoApiPaths = {
  nextSession: '/api/sessions/next',
  currentStatus: (sessionId: number) =>
    `/api/sessions/${sessionId}/current-status`,
  bingoCard: (bingoGameId: number) => `/api/bingo-card/${bingoGameId}`,
  chatMessages: (sessionId: number) => `/api/chat/messages/${sessionId}`,
  sendMessage: '/api/chat/send-message'
};

export const bingoSessionParams = {
  TYPE: 'type',
  STORE_ID: 'store_id',
  LANG: 'lang'
} as const;

export const bingoSessionTypes = {
  BINGO: 'bingo'
} as const;

export const bingoPrizeTypes = {
  PRODUCT: 'product',
  POINTS: 'point',
  VOUCHER: 'voucher',
  COUPON: 'coupon'
} as const;

export const bingoColumns = ['B', 'I', 'N', 'G', 'O'] as const;

export const bingoBallColorClasses = {
  RED: 'hb-bingo-ball-red',
  LIGHT_BLUE: 'hb-bingo-ball-light-blue',
  YELLOW: 'hb-bingo-ball-yellow',
  GREEN: 'hb-bingo-ball-green',
  DARK_BLUE: 'hb-bingo-ball-dark-blue'
} as const;

export const bingoStateClasses = {
  ANIMATING: 'is-animating',
  ENTERING: 'is-entering',
  LEAVING: 'is-leaving',
  DONE: 'is-done'
} as const;

export const bingoSelectors = {
  WRAPPER_ID: 'wv-wrapper',
  MOUNT_ID: 'bingo-page',
  WARMUP_DATA_KEY: 'warmupSeconds',
  MUTED_ATTRIBUTE: 'is-muted',
  GAME_CONTAINER: 'hb-bingo-game-container',
  LIVE_OVERLAY: 'hb-bingo-live-overlay',
  GAME_END_CONTAINER: 'hb-bingo-game-end-container',
  CAROUSEL_ITEM_TOP: 'hb-bingo-carousel-item is-top',
  CAROUSEL_ITEM_BOTTOM: 'hb-bingo-carousel-item is-bottom',
  FINDING: 'hb-bingo-finding',
  LIVERY_PLAYER_TAG: 'livery-player'
} as const;

export const bingoSteps = {
  MSG_1: 'msg-1',
  MSG_2: 'msg-2',
  FINDING: 'finding',
  PRIZES: 'prizes',
  CONSOLATION: 'consolation'
} as const;

export const bingoGameStates = {
  READY: 'ready',
  STARTED: 'started',
  COMPLETED: 'completed'
} as const;

export const bingoSessionStates = {
  DRAFT: 'draft',
  READY: 'ready',
  STARTED: 'started',
  OUTRO: 'outro',
  COMPLETED: 'completed'
} as const;

export const bingoPrizeWonTypes = {
  PRIZE: 'prize',
  CONSOLATION: 'consolation'
} as const;

export const bingoLobbyPhases = {
  LOADING: 'loading',
  STAY_TUNED: 'stayTuned',
  WEEKDAY: 'weekday',
  COUNTDOWN: 'countdown',
  LIVE: 'live'
} as const;

export const bingoLobbyTiming = {
  weekdayThresholdSeconds: 24 * 60 * 60,
  tickIntervalMs: 1000,
  fallbackWarmupSeconds: 6 * 60
} as const;

export type CarouselSpinStage = {
  starts: number;
  moves: number;
  time: number;
  timer: number;
  end?: true;
};

export const carouselSpinStages: CarouselSpinStage[] = [
  { starts: 0, moves: 21, time: 903, timer: 43 },
  { starts: 21, moves: 3, time: 171, timer: 57 },
  { starts: 25, moves: 2, time: 150, timer: 75 },
  { starts: 27, moves: 2, time: 180, timer: 90 },
  { starts: 28, moves: 1, time: 115, timer: 115 },
  { starts: 29, moves: 1, time: 135, timer: 135 },
  { starts: 30, moves: 1, time: 175, timer: 175 },
  { starts: 31, moves: 1, time: 200, timer: 200 },
  { starts: 32, moves: 3, time: 900, timer: 270, end: true }
];

export const carouselLimits = {
  MIN_ITEMS_TO_SPIN: 7,
  MAX_CAROUSEL_WINNERS: 11
};

export const vibratePattern = [100, 30, 100, 30, 100, 30, 800];

export const messageAvatarColors = [
  '#F53486',
  '#1BA2F2',
  '#3C6410',
  '#BF1112',
  '#8B118A',
  '#F6BA2B',
  '#F74D1D',
  '#0238AA'
];

export const chatLimits = {
  VISIBLE_MESSAGES: 5,
  MAX_MESSAGE_LENGTH: 100,
  DEFAULT_CHAT_HEIGHT: 250
};

export const chatTimings = {
  sendFailedNoticeMs: 5000
} as const;

export const bingoOutroUtm = {
  utm_source: 'bingo',
  utm_medium: 'app',
  utm_campaign: 'outro_link'
};

export const bingoLocaleCodes = ['da', 'en', 'no', 'nb', 'sv', 'de'];

// Android falls back to this pattern when `DateTimeFormatter.ofPattern` rejects the
// translated one rather than throwing. iOS has no equivalent guard, so the web
// follows Android, the stricter of the two.
export const bingoLobbyFormat = {
  safePattern: 'dd/MM/yyyy - hh:mm'
} as const;

export const bingoLobbyKeys = {
  NEXT_GAME_STARTS_IN: 'bingo.lobby.nextGameStartsIn',
  NEXT_GAME_STARTS_ON: 'bingo.lobby.nextGameStartsOn',
  DAY: 'bingo.lobby.day',
  HOURS: 'bingo.lobby.hours',
  MINUTES: 'bingo.lobby.minutes',
  SECONDS: 'bingo.lobby.seconds',
  STAY_TUNED: 'bingo.lobby.stayTuned',
  ANNOUNCEMENT_DATE: 'bingo.lobby.announcementDate',
  DATE_TIME_FORMAT: 'bingo.lobby.dateTimeFormat'
} as const;

export const bingoLandingKeys = {
  CTA_SIGNED_OUT: 'bingo.landing.ctaSignedOut',
  CTA_NOTIFY: 'bingo.landing.ctaNotify',
  CTA_JOIN: 'bingo.landing.ctaJoin',
  LIVE_NOW: 'bingo.landing.liveNow'
} as const;

export const bingoCtaSelectors = {
  CTA: 'hb-bingo-cta'
} as const;

export const bingoCtaCopyAttributes = {
  SIGNED_OUT: 'data-cta-signed-out',
  NOTIFY: 'data-cta-notify',
  JOIN: 'data-cta-join',
  LIVE_NOW: 'data-cta-live-now'
} as const;

export const bingoCustomerAttributes = {
  FIRST_NAME: 'data-customer-first-name',
  LAST_NAME: 'data-customer-last-name'
} as const;

export const bingoPlayerFrameAttributes = {
  WIDTH: 'data-player-width',
  ASPECT: 'data-player-aspect'
} as const;

export const bingoPlayerFrameDefaults = {
  width: 400,
  aspect: '9 / 16'
} as const;

// Must stay in sync with player_width's min/max in sections/bingo-player.liquid.
export const bingoPlayerWidthSchemaBounds = {
  min: 400,
  max: 800
} as const;

export const bingoBodyAttributes = {
  LIVE: 'data-bingo-live'
} as const;

export const bingoLobbySelectors = {
  LOBBY: 'hb-bingo-lobby',
  BACKGROUND: 'hb-bingo-lobby-bg',
  CONTENT: 'hb-bingo-lobby-content',
  COUNTDOWN: 'hb-bingo-lobby-countdown',
  COUNTDOWN_ROW: 'hb-bingo-lobby-countdown-row',
  COUNTDOWN_UNIT: 'hb-bingo-lobby-countdown-unit',
  WEEKDAY: 'hb-bingo-lobby-weekday',
  WEEKDAY_NAME: 'hb-bingo-lobby-weekday-name',
  STAY_TUNED: 'hb-bingo-lobby-stay-tuned',
  HEADING: 'hb-bingo-lobby-heading',
  DATE_LINE: 'hb-bingo-lobby-date'
} as const;

// Android shrinks the 96 dp weekday by this factor, repeatedly, until the single line
// fits its container.
export const bingoLobbyWeekdayFit = {
  SHRINK_STEP: 0.9,
  SHRINK_PROPERTY: '--hb-weekday-shrink'
} as const;

// The spiral calendar is Android's glyph and the one the Paparazzi snapshots render.
// iOS uses the tear-off calendar; we follow Android.
export const bingoLobbyGlyphs = {
  CALENDAR: '\u{1F5D3}\u{FE0F}',
  QUESTION_MARK: '?'
} as const;

export const liveryEvents = {
  ERROR: 'livery-error',
  RECOVERED: 'livery-recovered',
  PLAYBACK_CHANGE: 'livery-playback-change',
  CONFIG_CHANGE: 'livery-config-change'
} as const;

export const liveryStreamPhases = {
  PRE: 'PRE',
  LIVE: 'LIVE',
  POST: 'POST'
} as const;

export const bingoStreamStates = {
  CONNECTING: 'connecting',
  BUFFERING: 'buffering',
  PLAYING: 'playing',
  FAILED: 'failed'
} as const;

// iOS values: BingoViewModel.Constants.timerInterval (1.0 s) and
// loadingTimeBadConnectionThreshold (3.0 s), BingoViewController.Constants.initialTimeout
// (8.0 s). The two thresholds are alternatives, not tiers: only the token refresh on
// appear and foreground passes 8 s, every buffering event falls back to 3 s.
export const bingoStreamTiming = {
  tickMs: 1000,
  initialThresholdMs: 8000,
  stallThresholdMs: 3000
} as const;

export const bingoStreamRetry = {
  backoffMs: [5000, 15000, 45000],
  repeatMs: 60000,
  bufferingGraceMs: 10000,
  sustainedPlayMs: 30000,
  maxAttempts: 10
} as const;

// A page left open all evening could otherwise report a failure spell a second, so the
// per-load budget keeps one stuck client from dominating the data set.
export const bingoVideoTelemetry = {
  maxSpells: 20
} as const;

export const bingoStreamSelectors = {
  CONNECTION_BANNER: 'hb-bingo-connection-banner',
  CONNECTION_SPINNER: 'hb-bingo-connection-spinner',
  CONNECTION_TEXT: 'hb-bingo-connection-text',
  ERROR_SCREEN: 'hb-bingo-error',
  ERROR_LOGO: 'hb-bingo-error-logo',
  ERROR_EMOJI: 'hb-bingo-error-emoji',
  ERROR_TITLE: 'hb-bingo-error-title',
  ERROR_SUBTITLE: 'hb-bingo-error-subtitle',
  ERROR_BODY: 'hb-bingo-error-body',
  RETRY_BUTTON: 'hb-bingo-error-retry'
} as const;

export const bingoStreamKeys = {
  CONNECTING: 'bingo.stream.connecting'
} as const;

export const bingoErrorKeys = {
  TITLE: 'bingo.error.title',
  SUBTITLE: 'bingo.error.subtitle',
  BODY: 'bingo.error.body',
  RETRY: 'bingo.error.retry'
} as const;

export const bingoErrorGlyphs = {
  SWEAT_FACE: '\u{1F613}'
} as const;

export const browserNetworkEvents = {
  ONLINE: 'online',
  OFFLINE: 'offline'
} as const;
